import React, {useCallback} from "react";
import {PrimaryButton, PureButton, SecondaryLink} from "components/Common";
import {useDispatch, useSelector} from "react-redux";
import styled from "@emotion/styled";
import {partial} from "lodash";
import {useTranslation} from "react-i18next";
import {
	getFanVoteModalData,
	getFanVoteShareImage,
	getIsAllTeamPicked,
	getIsAllVotePicked,
	getPlayerById,
	getUser,
} from "modules/selectors";
import {closeFanVoteSavedModal} from "modules/actions";
import {GameType, Language, ShareTarget} from "modules/enums";
import {ConnextraType, createConnextraScriptTag, share} from "modules/utils";
import {BaseModal} from "components/Modals/BaseModal";
import {ButtonsBox, ModalText, ModalTitle} from "components/Modals/common";
import {Exist, FanVoteShareImage} from "components";
import {ReactComponent as DownloadIcon} from "assets/images/icons/download.svg";
import {ReactComponent as FacebookIcon} from "assets/images/icons/facebook.svg";
import {ReactComponent as TwitterIcon} from "assets/images/icons/twitter.svg";
import {useLocation} from "react-router-dom";

const ShareBox = styled.div`
	display: flex;
	margin-top: 20px;
	justify-content: center;
	gap: 24px;
`;

const getModalText = (isAllVotePicked: boolean, isAllTeamPicked: boolean) => {
	if (isAllVotePicked && isAllTeamPicked) {
		return "modal.fan_vote_saved.all_voted_text";
	}

	if (isAllVotePicked) {
		return "modal.fan_vote_saved.voted_text";
	}

	return "modal.fan_vote_saved.text";
};

const getUri = (search: string, id: number) => {
	if (search) {
		return `/picks${search}&id=${id}`;
	}
	return `/picks?id=${id}`;
};

export const FanVoteSavedModal: React.FC = () => {
	const dispatch = useDispatch();
	const {search} = useLocation();
	const {t, i18n} = useTranslation();
	const lang = i18n.language as Language;
	const data = useSelector(getFanVoteModalData);
	const user = useSelector(getUser)!;
	const player = useSelector(getPlayerById)(data?.playerId);
	const shareImage = useSelector(getFanVoteShareImage);
	const isAllTeamPicked = useSelector(getIsAllTeamPicked);
	const isAllVotePicked = useSelector(getIsAllVotePicked);

	const closeModal = useCallback(() => {
		dispatch(closeFanVoteSavedModal());
	}, [dispatch]);

	const shareHandler = useCallback(
		(target: ShareTarget) => {
			if (data) {
				void share({
					voteId: data.voteId,
					target,
					locale: lang,
					game: GameType.FanFavorite,
				});

				createConnextraScriptTag(ConnextraType.SHARE, user.id);
			}
		},
		[lang, data, user]
	);

	if (!player || !data) {
		return null;
	}

	return (
		<BaseModal handleClose={closeModal}>
			<FanVoteShareImage voteId={data.voteId} />
			<ModalTitle>{t("modal.fan_vote_saved.title")}</ModalTitle>
			<ModalText>{t(getModalText(isAllVotePicked, isAllTeamPicked))}</ModalText>

			<ButtonsBox>
				<Exist when={!isAllTeamPicked}>
					<SecondaryLink to={getUri(search, player.id)} onClick={closeModal}>
						{t("modal.fan_vote_saved.add")}
					</SecondaryLink>
				</Exist>

				<PrimaryButton onClick={closeModal}>
					{t("modal.fan_vote_saved.close")}
				</PrimaryButton>
			</ButtonsBox>

			<ShareBox>
				{shareImage && (
					<a href={shareImage} download>
						<DownloadIcon />
					</a>
				)}

				<PureButton onClick={partial(shareHandler, ShareTarget.Facebook)}>
					<FacebookIcon />
				</PureButton>

				<PureButton onClick={partial(shareHandler, ShareTarget.Twitter)}>
					<TwitterIcon />
				</PureButton>
			</ShareBox>
		</BaseModal>
	);
};
